/* global React, Icon, Modal, t */
(function (global) {
  const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);
  const MAX_SOURCE_BYTES = 20 * 1024 * 1024;
  const MAX_SOURCE_TOTAL_BYTES = 40 * 1024 * 1024;
  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  const MAX_TOTAL_BYTES = 10 * 1024 * 1024;
  const MAX_DIMENSION = 1920;
  const OPTIMIZE_FROM_BYTES = 350 * 1024;

  const emptySupportMessageDraft = () => ({ body: '', images: [], optimizing: false });
  const supportMessageDraftHasContent = (draft) => Boolean(String(draft?.body || '').trim() || draft?.images?.length);
  const supportWebpName = (name) => `${String(name || 'image').replace(/\.[^.]+$/, '').trim() || 'image'}.webp`;
  const supportImageSize = (width, height) => {
    if (!(width > 0) || !(height > 0)) throw new Error('Invalid image dimensions.');
    const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
    return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
  };
  const supportCanvasBlob = (canvas) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Image conversion failed.')), 'image/webp', 0.82);
  });
  const optimizeSupportImage = async (file) => {
    if (file.type === 'image/gif' || file.size < OPTIMIZE_FROM_BYTES) return file;
    let bitmap;
    try {
      bitmap = await window.createImageBitmap(file, { imageOrientation: 'from-image' });
      const target = supportImageSize(bitmap.width, bitmap.height);
      const canvas = document.createElement('canvas');
      canvas.width = target.width;
      canvas.height = target.height;
      const context = canvas.getContext('2d', { alpha: true });
      if (!context) return file;
      context.drawImage(bitmap, 0, 0, target.width, target.height);
      const blob = await supportCanvasBlob(canvas);
      return blob.size < file.size
        ? new File([blob], supportWebpName(file.name), { type: 'image/webp', lastModified: file.lastModified })
        : file;
    } catch (error) {
      return file;
    } finally {
      bitmap?.close?.();
    }
  };
  const supportFileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Image could not be read.'));
    reader.onload = () => resolve(String(reader.result || '').split(',', 2)[1] || '');
    reader.readAsDataURL(file);
  });
  const prepareSupportMessageRequest = async (draft) => ({
    body: String(draft?.body || '').trim(),
    ...(draft?.richText?.length ? { richText: draft.richText } : {}),
    ...(draft?.images?.length ? {
      attachments: await Promise.all(draft.images.map(async ({ file }) => ({
        name: file.name || 'pasted-image.png',
        mimeType: file.type,
        size: file.size,
        dataBase64: await supportFileToBase64(file),
      }))),
    } : {}),
  });

  const sameMarks = (left, right) => left.bold === right.bold && left.italic === right.italic
    && left.underline === right.underline && left.link === right.link;
  const spansFromNode = (node, marks = {}) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ? [{ text: node.textContent, ...marks }] : [];
    if (!(node instanceof HTMLElement)) return [];
    if (node.tagName === 'BR') return [{ text: '\n', ...marks }];
    const next = { ...marks };
    if (['B', 'STRONG'].includes(node.tagName)) next.bold = true;
    if (['I', 'EM'].includes(node.tagName)) next.italic = true;
    if (node.tagName === 'U') next.underline = true;
    if (node.tagName === 'A' && /^https?:\/\//i.test(node.getAttribute('href') || '')) next.link = node.getAttribute('href');
    return Array.from(node.childNodes).flatMap((child) => spansFromNode(child, next));
  };
  const mergeSpans = (spans) => spans.reduce((result, span) => {
    if (!span.text) return result;
    const last = result[result.length - 1];
    if (last && sameMarks(last, span)) last.text += span.text;
    else result.push({ ...span });
    return result;
  }, []);
  const blockFromNode = (node, type) => ({ type, spans: mergeSpans(spansFromNode(node)) });
  const supportRichTextFromEditor = (editor) => {
    const blocks = [];
    let looseSpans = [];
    const flushLoose = () => {
      if (looseSpans.length) blocks.push({ type: 'paragraph', spans: mergeSpans(looseSpans) });
      looseSpans = [];
    };
    Array.from(editor.childNodes).forEach((node) => {
      if (node instanceof HTMLElement && ['UL', 'OL'].includes(node.tagName)) {
        flushLoose();
        const type = node.tagName === 'UL' ? 'bullet-list-item' : 'ordered-list-item';
        Array.from(node.children).filter((child) => child.tagName === 'LI').forEach((child) => blocks.push(blockFromNode(child, type)));
      } else if (node instanceof HTMLElement && ['DIV', 'P'].includes(node.tagName)) {
        flushLoose();
        blocks.push(blockFromNode(node, 'paragraph'));
      } else if (node instanceof HTMLElement && node.tagName === 'BR') {
        flushLoose();
        blocks.push({ type: 'paragraph', spans: [] });
      } else {
        looseSpans.push(...spansFromNode(node));
      }
    });
    flushLoose();
    const body = blocks.map((block) => {
      const text = block.spans.map((span) => span.text).join('');
      return block.type === 'bullet-list-item' ? `• ${text}` : text;
    }).join('\n').trim();
    const formatted = blocks.some((block) => block.type !== 'paragraph'
      || block.spans.some((span) => span.bold || span.italic || span.underline || span.link));
    return { body, ...(formatted ? { richText: blocks } : {}) };
  };

  function SupportMessageComposer({ value, onChange, disabled, placeholder, allowScreenshot = false, onCaptureChange }) {
    const editorRef = React.useRef(null);
    const fileRef = React.useRef(null);
    const latestRef = React.useRef(value);
    const optimizingRef = React.useRef(false);
    const mountedRef = React.useRef(true);
    const previousImagesRef = React.useRef(value.images || []);
    const lastBodyRef = React.useRef('');
    const [error, setError] = React.useState('');
    const [capturing, setCapturing] = React.useState(false);
    latestRef.current = value;

    React.useEffect(() => {
      if (!editorRef.current || value.body === lastBodyRef.current) return;
      editorRef.current.textContent = value.body || '';
      lastBodyRef.current = value.body || '';
    }, [value.body]);
    React.useEffect(() => {
      const next = value.images || [];
      previousImagesRef.current.filter((image) => !next.some((entry) => entry.id === image.id)).forEach((image) => URL.revokeObjectURL(image.previewUrl));
      previousImagesRef.current = next;
    }, [value.images]);
    React.useEffect(() => () => {
      mountedRef.current = false;
      previousImagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    }, []);

    const emit = () => {
      if (!editorRef.current) return;
      const content = supportRichTextFromEditor(editorRef.current);
      lastBodyRef.current = content.body;
      onChange({ ...latestRef.current, ...content, richText: content.richText });
    };
    const command = (name, argument) => {
      editorRef.current?.focus();
      document.execCommand(name, false, argument);
      emit();
    };
    const addLink = () => {
      const value = window.prompt(t('support.editor.linkPrompt'));
      if (!value) return;
      try {
        const url = new URL(value);
        if (!['http:', 'https:'].includes(url.protocol)) throw new Error('protocol');
        command('createLink', url.toString());
      } catch (linkError) {
        setError(t('support.error.link'));
      }
    };
    const addImages = async (files) => {
      const current = latestRef.current;
      if (disabled || current.optimizing || optimizingRef.current || !files.length) return;
      setError('');
      if (files.some((file) => !IMAGE_TYPES.has(file.type))) return setError(t('support.error.imageType'));
      if (files.some((file) => file.size > MAX_SOURCE_BYTES)) return setError(t('support.error.imageSourceSize'));
      if ((current.images?.length || 0) + files.length > 4) return setError(t('support.error.imageCount'));
      if (files.reduce((sum, file) => sum + file.size, 0) > MAX_SOURCE_TOTAL_BYTES) return setError(t('support.error.imageSourceTotal'));
      optimizingRef.current = true;
      onChange({ ...current, optimizing: true });
      try {
        const optimized = await Promise.all(files.map(optimizeSupportImage));
        if (!mountedRef.current) return;
        if (optimized.some((file) => file.size > MAX_IMAGE_BYTES)) {
          setError(t('support.error.imageSize'));
          onChange({ ...latestRef.current, optimizing: false });
          return;
        }
        const latest = latestRef.current;
        if ([...(latest.images || []).map((image) => image.file), ...optimized].reduce((sum, file) => sum + file.size, 0) > MAX_TOTAL_BYTES) {
          setError(t('support.error.imageTotal'));
          onChange({ ...latest, optimizing: false });
          return;
        }
        onChange({
          ...latest,
          optimizing: false,
          images: [...(latest.images || []), ...optimized.map((file) => ({ id: crypto.randomUUID(), file, previewUrl: URL.createObjectURL(file) }))],
        });
      } catch (imageError) {
        if (!mountedRef.current) return;
        setError(t('support.error.imageOptimize'));
        onChange({ ...latestRef.current, optimizing: false });
      } finally {
        optimizingRef.current = false;
      }
    };
    const paste = (event) => {
      const images = Array.from(event.clipboardData.files || []).filter((file) => file.type.startsWith('image/'));
      if (!images.length) return;
      event.preventDefault();
      void addImages(images);
    };
    const captureScreenshot = async () => {
      if (disabled || value.optimizing || capturing) return;
      setError('');
      if ((latestRef.current.images?.length || 0) >= 4) {
        setError(t('support.error.imageCount'));
        return;
      }
      setCapturing(true);
      onCaptureChange?.(true);
      try {
        const file = await window.PlummeSupportScreenshot.captureVisiblePage();
        await addImages([file]);
      } catch (captureError) {
        if (mountedRef.current) setError(t('support.error.screenshot'));
      } finally {
        if (mountedRef.current) setCapturing(false);
        onCaptureChange?.(false);
      }
    };
    const keyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        editorRef.current?.closest('form')?.requestSubmit();
      }
    };
    const editorDisabled = disabled || value.optimizing || capturing;
    return <div className="support-message-composer">
      <div className="support-rich-editor" data-disabled={editorDisabled || undefined}>
        <div className="support-rich-toolbar" role="toolbar" aria-label={t('support.editor.toolbar')}>
          <button type="button" aria-label={t('support.editor.bold')} disabled={editorDisabled} onMouseDown={(event) => { event.preventDefault(); command('bold'); }}><strong>B</strong></button>
          <button type="button" aria-label={t('support.editor.italic')} disabled={editorDisabled} onMouseDown={(event) => { event.preventDefault(); command('italic'); }}><em>I</em></button>
          <button type="button" aria-label={t('support.editor.underline')} disabled={editorDisabled} onMouseDown={(event) => { event.preventDefault(); command('underline'); }}><u>U</u></button>
          <button type="button" aria-label={t('support.editor.bullets')} disabled={editorDisabled} onMouseDown={(event) => { event.preventDefault(); command('insertUnorderedList'); }}>• {t('support.editor.list')}</button>
          <button type="button" aria-label={t('support.editor.numbered')} disabled={editorDisabled} onMouseDown={(event) => { event.preventDefault(); command('insertOrderedList'); }}>1. {t('support.editor.list')}</button>
          <button type="button" disabled={editorDisabled} onMouseDown={(event) => event.preventDefault()} onClick={addLink}>↗ {t('support.editor.link')}</button>
          <button type="button" disabled={editorDisabled} onMouseDown={(event) => event.preventDefault()} onClick={() => fileRef.current?.click()}>▧ {t('support.editor.image')}</button>
          {allowScreenshot && <button type="button" disabled={editorDisabled} aria-label={t('support.editor.screenshotLabel')} title={t('support.editor.screenshotLabel')} onMouseDown={(event) => event.preventDefault()} onClick={captureScreenshot} data-help={t('helpTopics.support.body')} data-help-title={t('helpTopics.support.title')}><Icon name="monitor" size={14} />{t('support.editor.screenshot')}</button>}
          <input ref={fileRef} hidden type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple onChange={(event) => { void addImages(Array.from(event.target.files || [])); event.target.value = ''; }} />
        </div>
        <div ref={editorRef} className="support-rich-input" contentEditable={!editorDisabled} role="textbox" aria-multiline="true" aria-label={placeholder} data-placeholder={placeholder} onInput={emit} onPaste={paste} onKeyDown={keyDown} suppressContentEditableWarning />
      </div>
      {!!value.images?.length && <div className="support-pending-images" aria-label={t('support.editor.pendingImages')}>
        {value.images.map((image) => <div key={image.id} className="support-pending-image">
          <img src={image.previewUrl} alt={image.file.name || t('support.editor.image')} />
          <button type="button" disabled={editorDisabled} aria-label={t('support.editor.removeImage')} onClick={() => onChange({ ...latestRef.current, images: latestRef.current.images.filter((entry) => entry.id !== image.id) })}>×</button>
          <small>{image.file.name}</small>
        </div>)}
      </div>}
      {value.optimizing && <div className="support-optimizer-status">{t('support.editor.optimizing')}</div>}
      {capturing && <div className="support-optimizer-status">{t('support.editor.capturing')}</div>}
      {error && <div className="support-error" role="alert">{error}</div>}
    </div>;
  }

  const renderSupportSpan = (span, key) => {
    let content = span.text;
    if (span.bold) content = <strong>{content}</strong>;
    if (span.italic) content = <em>{content}</em>;
    if (span.underline) content = <u>{content}</u>;
    if (span.link && /^https?:\/\//i.test(span.link)) content = <a href={span.link} target="_blank" rel="noreferrer">{content}</a>;
    return <React.Fragment key={key}>{content}</React.Fragment>;
  };
  function SupportMessageContent({ message }) {
    const [preview, setPreview] = React.useState(null);
    const blocks = message.richText?.length ? message.richText : null;
    let orderedIndex = 0;
    return <React.Fragment>
      <div className="support-bubble-text">{blocks ? blocks.map((block, blockIndex) => {
        const content = block.spans.map((span, spanIndex) => renderSupportSpan(span, `${blockIndex}-${spanIndex}`));
        if (block.type === 'bullet-list-item') { orderedIndex = 0; return <div key={blockIndex} className="support-rich-list-item">• <span>{content}</span></div>; }
        if (block.type === 'ordered-list-item') { orderedIndex += 1; return <div key={blockIndex} className="support-rich-list-item">{orderedIndex}. <span>{content}</span></div>; }
        orderedIndex = 0;
        return <div key={blockIndex}>{content}</div>;
      }) : message.body}</div>
      {!!message.attachments?.length && <div className="support-message-images">{message.attachments.map((attachment) => <button type="button" key={attachment.id} aria-label={t('support.editor.openImage')} onClick={() => setPreview(attachment)}><img src={attachment.url} alt={attachment.name} loading="lazy" decoding="async" /></button>)}</div>}
      {preview && <Modal open portal onClose={() => setPreview(null)} title={preview.name} max={960}><div className="support-image-modal"><img src={preview.url} alt={preview.name} /></div></Modal>}
    </React.Fragment>;
  }

  Object.assign(window, {
    SupportMessageComposer,
    SupportMessageContent,
    emptySupportMessageDraft,
    prepareSupportMessageRequest,
    supportMessageDraftHasContent,
    supportImageSize,
    supportRichTextFromEditor,
    supportWebpName,
  });
})(typeof window !== 'undefined' ? window : globalThis);
